import styles from "./CustomDatePicker.module.css";
import React, { useRef, useEffect, useState } from 'react';

export const CustomDatePicker = ({ 
  value, 
  onChange, 
  minDate,
  placeholder = "Select date",
  disabled = false,
  width = '200px'
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const datePickerRef = useRef(null);

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };

    if (showPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPicker]);

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return placeholder;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const navigateMonth = (direction) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1));
  };

  const isDateSelected = (day) => {
    if (!value) return false;
    const selectedDate = new Date(value);
    return (
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === currentDate.getMonth() &&
      selectedDate.getFullYear() === currentDate.getFullYear()
    );
  };

  const isToday = (day) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  const isDateDisabled = (day) => {
    if (!minDate) return false;
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const min = new Date(minDate);
    return date < min;
  };

  const handleDateSelect = (day) => {
    if (isDateDisabled(day)) return;
    
    const selectedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day, 23, 59, 59);
    onChange(selectedDate.toISOString());
    setShowPicker(false);
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className={styles.calendarEmptyCell}></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const selected = isDateSelected(day);
      const today = isToday(day);
      const disabled = isDateDisabled(day);
      
      days.push(
        <div
          key={day}
          className={`${styles.calendarDay} 
            ${selected ? styles.calendarDaySelected : ''} 
            ${today ? styles.calendarDayToday : ''}
            ${disabled ? styles.calendarDayDisabled : ''}`}
          onClick={() => !disabled && handleDateSelect(day)}
        >
          {day}
        </div>
      );
    }

    return (
      <div className={styles.calendar}>
        <div className={styles.calendarHeader}>
          <button 
            type="button" 
            onClick={() => navigateMonth(-1)}
            className={styles.calendarNavButton}
          >
            ‹
          </button>
          <div className={styles.calendarTitle}>
            {currentDate.toLocaleString('default', { month: 'long' })} {currentDate.getFullYear()}
          </div>
          <button 
            type="button" 
            onClick={() => navigateMonth(1)}
            className={styles.calendarNavButton}
          >
            ›
          </button>
        </div>
        
        <div className={styles.calendarWeekdays}>
          <div className={styles.calendarWeekday}>Sun</div>
          <div className={styles.calendarWeekday}>Mon</div>
          <div className={styles.calendarWeekday}>Tue</div>
          <div className={styles.calendarWeekday}>Wed</div>
          <div className={styles.calendarWeekday}>Thu</div>
          <div className={styles.calendarWeekday}>Fri</div>
          <div className={styles.calendarWeekday}>Sat</div>
        </div>
        
        <div className={styles.calendarGrid}>
          {days}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.customDatePicker} style={{ width }} ref={datePickerRef}>
      <div 
        className={`${styles.dateInputDisplay} ${disabled ? styles.disabled : ''}`}
        onClick={() => !disabled && setShowPicker(!showPicker)}
      >
        <span className={styles.dateText}>{formatDateForDisplay(value)}</span>
        
      </div>
      
      {showPicker && (
        <div className={styles.datePickerDropdown}>
          {renderCalendar()}
        </div>
      )}
    </div>
  );
};